import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { EVISORT_URL_REGEXP } from '~/common/constants/regexp';
import { Actions } from '~/eds/src/components/Actions/Actions';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { Feedback } from '~/eds/src/modules/Feedback/Feedback';
import { Nullable } from '~/eds/src/types/Nullable';
import { User } from '~/eds/src/types/User';
import { UserAction } from '~/eds/src/types/UserAction';
import { getUserName } from '~/eds/src/utils/getUserName';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import chatbot from '~/redux/slices/chatbot';
import { useRouting } from '~/routing/useRouting';
import { getApplicationRoute } from '~/routing/utils';

import {
  Citation,
  FeedbackDetails,
  Message as MessageType,
} from '../types/chat';
import { BlinkingCursor } from './BlinkingCursor';
import { SourcesList } from './components/Sources/SourcesList';
import { CHOICE_DISPLAY_LENGTH, MESSAGE_ACTIONS_CLASSNAME } from './constants';
import { ThinkingEvents } from './ThinkingEvents';
import { createMarkdownComponents, testIsTicketCitation } from './utils';

interface Props {
  message: MessageType;
  user: User;
  onChoose: (message: MessageType, choice: string) => void;
  onSubmitFeedback: (
    message: MessageType,
    feedbackDetails: FeedbackDetails,
  ) => void;
  onSelectSource?: (message: MessageType, sourceIndex: number) => void;
  disableChoices?: boolean;
  isLoading?: boolean;
  isReplying?: boolean;
  messageActions: UserAction[];
  thinkingEvents?: string[];
  shouldShowActions: boolean;
}

export const AiMessage = ({
  disableChoices,
  isLoading = false,
  isReplying = false,
  message,
  messageActions,
  thinkingEvents,
  shouldShowActions,
  user,
  onChoose,
  onSelectSource,
  onSubmitFeedback,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const { location, navigate } = useRouting();
  const dispatch = useDispatch();
  const [resolveCitation] = api.endpoints.resolveCitation.useLazyQuery();
  const handleSubmitFeedback = (value: Nullable<boolean>, comment?: string) => {
    if (message) {
      onSubmitFeedback(message, { value, comment });
    }
  };
  const { choices = [], text, timestamp, citations = [] } = message ?? {};
  const tooltip = `${getUserName({ user, intl })}\n${formatDate(
    timestamp,
    'short_datetime',
  )}`;

  const messageChoices = useMemo(
    () =>
      (message?.choices ?? []).map((choice) => {
        /** TODO - `Button` should handle this truncation internally after design proposes the display length limit */
        const isLongText = choice.length > CHOICE_DISPLAY_LENGTH;
        return {
          disabled: disableChoices,
          level: 'secondary' as const,
          text: isLongText
            ? choice.substring(0, CHOICE_DISPLAY_LENGTH) + '…'
            : choice,
          tooltip: isLongText ? choice : undefined,
          onClick: () => {
            if (message) {
              onChoose(message, choice);
            }
          },
        };
      }),
    [disableChoices, message],
  );

  const handleTicketEntityCitationClick = async (citation: Citation) => {
    const resolvedCitation = await resolveCitation(citation.entity).unwrap();
    if (resolvedCitation) {
      handleCitationLinkAndHighlights({
        ...citation,
        link: resolvedCitation.link,
      });
    }
  };

  const handleCitationLinkAndHighlights = (citation: Citation) => {
    if (citation?.link) {
      const highlights = citation.highlights.map((highlight) => ({
        ...highlight,
        citationKey: citation.key,
      }));
      dispatch(chatbot.actions.setSources(highlights));
      dispatch(chatbot.actions.setActiveSourceIndex(0));
      const destinationRoute = testIsTicketCitation(citation)
        ? citation.link
        : getApplicationRoute(citation.link);

      if (location.pathname !== destinationRoute) {
        navigate(destinationRoute);
      }
    }
  };

  const handleCitationClick = (citation: Citation) => {
    dispatch(chatbot.actions.setActiveCitation(citation));
    if (testIsTicketCitation(citation)) {
      handleTicketEntityCitationClick(citation);
    } else {
      handleCitationLinkAndHighlights(citation);
    }
  };

  const markdownOptions = useMemo(
    () => ({
      allowLinks: {
        disabledTooltip: intl.formatMessage({
          id: 'AiMessage.externalLinkTooltip',
          defaultMessage:
            'This link is to an external source that cannot be verified by Evisort. Please manually verify the link before copy and pasting into your web browser.',
          description: 'Tooltip warning about external links',
        }),
        regexp: EVISORT_URL_REGEXP,
      },
    }),
    [intl],
  );

  const hasCitations = citations.length > 0;

  const markdownComponents = useMemo(
    () =>
      createMarkdownComponents(
        {
          citations: citations,
          onCitationClick: handleCitationClick,
          isReplying,
        },
        {
          shouldSkipLinks: hasCitations,
          externalLinkProps: markdownOptions.allowLinks,
        },
      ),
    [hasCitations],
  );

  return (
    <Layout direction="column" spacing={4} styles={componentStyles.message}>
      <Layout align="center">
        <Layout ml="-3px" alignSelf="flex-start">
          <Tooltip tooltip={tooltip} placement="left">
            <Icon
              name="evisort-ai-animated"
              size="ml"
              icon="evisort-ai-animated"
            />
          </Tooltip>
        </Layout>
        <Layout pl="13px" direction="column" w="100%">
          {!!thinkingEvents?.length && (
            <ThinkingEvents events={thinkingEvents} />
          )}
          {isLoading ? (
            <Layout direction="column" py={2} pr={2} spacing={1}>
              <LoadingShimmer height="20px" width="100%" />
              <LoadingShimmer height="20px" width="90%" />
              <LoadingShimmer height="20px" width="80%" />
            </Layout>
          ) : (
            <Markdown
              components={markdownComponents}
              options={markdownOptions}
              text={text}
            />
          )}
        </Layout>
      </Layout>
      {isReplying && !isLoading && Boolean(text) && (
        <Layout>
          <BlinkingCursor />
        </Layout>
      )}

      <Layout
        direction="column"
        flex="auto"
        justify="center"
        minW={0}
        position="relative"
        spacing={2}
      >
        {choices && <Actions wrap actions={messageChoices} size="s" />}
      </Layout>
      <SourcesList
        message={message}
        onSelectSource={onSelectSource}
        onSelectCitation={handleCitationClick}
      />
      {shouldShowActions && (
        <Layout
          alignSelf="flex-end"
          bg="background"
          borderRadius={4}
          className={MESSAGE_ACTIONS_CLASSNAME}
          styles={componentStyles.actions}
        >
          <Actions actions={messageActions} />
          {Boolean(messageActions.length) && (
            <Feedback
              value={message?.feedbackDetails?.value ?? null}
              onChange={handleSubmitFeedback}
              variant="subtle"
              placeholders={{
                like: intl.formatMessage({
                  id: 'AiMessage.whatWorkedWellPlaceholder',
                  defaultMessage:
                    'What made this response helpful? Specific examples help us improve like: "Found the right clause quickly" or "Clear explanation of payment terms".',
                  description:
                    'Placeholder for feedback comment textarea when user liked the message',
                }),
                dislike: intl.formatMessage({
                  id: 'AiMessage.whatCouldImprovePlaceholder',
                  defaultMessage:
                    'What would have made this better? Examples: "Wrong context (analyzed folder instead of document)" or "Missed key details in Section 3".',
                  description:
                    'Placeholder for feedback comment textarea when user disliked the message',
                }),
              }}
              submitDetails={{
                onSubmit: (submitDetails) => {
                  handleSubmitFeedback(
                    submitDetails.value,
                    submitDetails.comment,
                  );
                },
                minLength: 20,
              }}
            />
          )}
        </Layout>
      )}
    </Layout>
  );
};

const componentStyles = {
  message: {
    position: 'relative',
    [`&:hover .${MESSAGE_ACTIONS_CLASSNAME}`]: {
      visibility: 'visible',
    },
    [`&.current:hover + .other .${MESSAGE_ACTIONS_CLASSNAME}`]: {
      visibility: 'visible',
    },
    paddingTop: 1,
  },
  actions: {
    visibility: 'hidden',
  },
};
